import { findNodeById, flattenTree } from '../hooks/useConfigTree'
import { useConfigStore } from '../store/configStore'
import { clearModuleDraft, getModuleDraft, markModuleDraftClean } from '../store/moduleDraftStore'
import type { ConfigNode } from '../types/config.types'
import type { WorkspaceTab } from '../types/editor.types'
import { codeService } from './codeService'
import { configObjectApi } from './configObjectApi'
import { MODULE_CONFIG_OBJECT_TYPES } from './objectModuleTypes'

export type ResolvableObjectModule = {
  tabId: string
  moduleId: string
  objectName: string
  nodeId: string
}

/** Tab id `module-…` → codeService/draft moduleId (strip one leading `module-`). */
export function moduleIdFromTabId(tabId: string): string {
  return tabId.startsWith('module-') ? tabId.slice('module-'.length) : tabId
}

/** Object Editor tabs carry the object name, not the tree node id. */
export function findModuleObjectByName(tree: ConfigNode[], name: string | undefined): ConfigNode | undefined {
  if (!name) return undefined
  return flattenTree(tree).find((n) => n.name === name && MODULE_CONFIG_OBJECT_TYPES.has(n.type))
}

/** Drop in-memory module draft when a code tab is discarded/closed. */
export function discardCodeTabDraft(
  tab: Pick<WorkspaceTab, 'id' | 'editorType'> & Partial<Pick<WorkspaceTab, 'nodeId'>>,
): void {
  if (tab.editorType === 'configObject') {
    const node = findModuleObjectByName(useConfigStore.getState().tree, tab.nodeId)
    if (node) clearModuleDraft(`module-${node.id}`)
    return
  }
  if (tab.editorType !== 'code') return
  clearModuleDraft(moduleIdFromTabId(tab.id))
}

export function resolveObjectModuleTarget(
  tab: Pick<WorkspaceTab, 'id' | 'editorType' | 'nodeId' | 'nodeType' | 'isDirty'>,
  tree: ConfigNode[],
): ResolvableObjectModule | null {
  if (!tab.isDirty) return null
  // Object Editor ("Open object schema") edits the same draft; its tab carries the object name.
  if (tab.editorType === 'configObject') {
    const node = findModuleObjectByName(tree, tab.nodeId)
    if (!node) return null
    return { tabId: tab.id, moduleId: `module-${node.id}`, objectName: node.name, nodeId: node.id }
  }
  if (tab.editorType !== 'code') return null
  if (!MODULE_CONFIG_OBJECT_TYPES.has(tab.nodeType)) return null
  const node = findNodeById(tree, tab.nodeId)
  if (!node) return null
  if (!MODULE_CONFIG_OBJECT_TYPES.has(node.type)) return null
  return {
    tabId: tab.id,
    moduleId: moduleIdFromTabId(tab.id),
    objectName: node.name,
    nodeId: tab.nodeId,
  }
}

export async function flushObjectModuleCode(args: {
  connectionId: string
  objectName: string
  moduleId: string
  code: string
  nodeId: string
  updateNodePatch: (id: string, patch: Partial<ConfigNode>) => void
}): Promise<void> {
  const { connectionId, objectName, moduleId, code, nodeId, updateNodePatch } = args
  await configObjectApi.updateObject(connectionId, objectName, { module_code: code })
  await codeService.saveModule(moduleId, code)
  // Keep the tree node in step so the agent and the editor read the saved code.
  updateNodePatch(nodeId, { module_code: code })
  markModuleDraftClean(moduleId, code)
}

/**
 * Pure: which code an object-module editor should show.
 * A dirty draft wins; then the tree node when it carries the key (silence-vs-empty:
 * an empty string is authoritative); otherwise the saved/server code, then local.
 */
export function pickObjectModuleCode(args: {
  treeHasKey: boolean
  treeCode: string | null | undefined
  draft: string | null | undefined
  saved: string | null | undefined
  local: string | null | undefined
}): string {
  if (args.draft != null) return args.draft
  if (args.treeHasKey) return args.treeCode ?? ''
  return args.saved ?? args.local ?? ''
}

export async function flushDirtyObjectModules(args: {
  connectionId: string
  tabs: WorkspaceTab[]
  tree: ConfigNode[]
}): Promise<{ flushedTabIds: string[] }> {
  const { connectionId, tabs, tree } = args
  const flushedTabIds: string[] = []
  const flushedModuleIds = new Set<string>()
  for (const tab of tabs) {
    const target = resolveObjectModuleTarget(tab, tree)
    if (!target) continue
    // The Module tab and the Object Editor tab of one object share a draft: save it once.
    if (flushedModuleIds.has(target.moduleId)) {
      flushedTabIds.push(target.tabId)
      continue
    }
    flushedModuleIds.add(target.moduleId)
    const draft = getModuleDraft(target.moduleId)
    // No draft = nothing typed; never overwrite stored code with ''.
    if (!draft) continue
    const code = draft.code
    try {
      await flushObjectModuleCode({
        connectionId,
        objectName: target.objectName,
        moduleId: target.moduleId,
        code,
        nodeId: target.nodeId,
        updateNodePatch: useConfigStore.getState().updateNodePatch,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      throw new Error(`Failed to save module for ${target.objectName}: ${message}`)
    }
    flushedTabIds.push(target.tabId)
  }
  return { flushedTabIds }
}

/** Pure: whether a load from API/local may overwrite the editor. */
export function shouldApplyLoadedModuleCode(args: {
  draftDirty: boolean
  incomingCode: string | null | undefined
}): 'keep-draft' | 'apply' {
  if (args.draftDirty) return 'keep-draft'
  return 'apply'
}
